// PRD 07 §3.2 / §4.2 — Video content block editor.
//   Title (optional) + Video URL (required — https://, YouTube/Vimeo) + showText
//   toggle + optional description textarea.
//
// SHCT-648 — video is URL-ONLY. Direct MP4 file upload is OUT OF SCOPE per
// clarification ADM.018 ("영상을 직접 선택해서 업로드 하는 방식은 본 스코프에서 제외"),
// extended to 07 관리지침 by ADM.026 ("네 맞습니다"). PRD 07 §4.4 spells it out:
// "영상 블록은 dataUrl을 쓰지 않고 url 필드만 사용". The click-to-upload dropzone,
// its 20MB cap and the `dataUrl` write are removed here; the counseling-manual
// twin (PRD 11) was already converted the same way. The "최대 20MB" figure also had
// no source — 공통 시스템정의 §9's 20MB is the DOCUMENT limit, not a video one.

import { useTranslation } from 'react-i18next';
import { Trash2 } from 'lucide-react';

import { isAllowedVideoUrl } from '@/shared/utils/video-embed';

import type { ContentBlock } from '../types/management-guidelines.types';

// RULE_030_LINK_GUARD_01 (DH-MGMT-GUIDELINES-001): a video block URL is accepted only when its host
// is a YouTube/Vimeo one. UX pre-check only — the BE host check is authoritative.
//
// The allow-list used to be a private copy here that matched the host EXACTLY, so every sub-domain
// form was rejected — including `player.vimeo.com`, which the preview beside this editor embedded
// happily (QC 2026-08-12). ADM.014 puts no format restriction on Vimeo, so that link is valid. The
// rule now comes from the shared module every content screen and preview uses; see video-embed.ts.
//
// True when a non-empty URL is not an allowed video URL. An empty URL is NOT flagged here —
// emptiness is the form-level "required" check's job, and flagging it while the user is still
// typing the first character would be hostile.
function isVideoUrlNotAllowed(raw: string | null | undefined): boolean {
  const value = (raw ?? '').trim();
  if (!value) return false;
  return !isAllowedVideoUrl(value);
}

interface VideoBlockEditorProps {
  block: ContentBlock;
  index: number;
  onChange: (patch: Partial<ContentBlock>) => void;
  onDelete: () => void;
  /** SHCT-582 — PRD 21 §7.4 P-2: locked for a METADATA:READ operator. */
  disabled?: boolean;
}

export function VideoBlockEditor({
  block,
  index,
  onChange,
  onDelete,
  disabled = false,
}: VideoBlockEditorProps) {
  const { t } = useTranslation('management-guidelines');
  const urlNotAllowed = isVideoUrlNotAllowed(block.url);

  return (
    <div
      style={{
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-sm)',
        padding: 8,
        background: '#fff',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 8,
        }}
      >
        <span style={{ fontSize: 13, color: 'var(--muted-foreground)' }}>
          {t('blocks.position', {
            index: index + 1,
            type: t('blocks.types.video'),
          })}
        </span>
        <button
          type="button"
          onClick={onDelete}
          className="btn btn-default btn-secondary"
          disabled={disabled}
          style={{
            height: 28,
            padding: '0 8px',
            color: disabled ? undefined : 'var(--destructive)',
          }}
          title={t('blocks.delete')}
          aria-label={t('blocks.delete')}
        >
          <Trash2 size={14} />
        </button>
      </div>

      <input
        type="text"
        className="form-field__input"
        value={block.title}
        onChange={(e) => onChange({ title: e.target.value })}
        placeholder={t('blocks.videoTitlePlaceholder')}
        disabled={disabled}
        style={{ marginBottom: 6 }}
      />

      <input
        type="url"
        inputMode="url"
        className="form-field__input"
        value={block.url ?? ''}
        onChange={(e) => onChange({ url: e.target.value })}
        placeholder={t('blocks.videoUrlPlaceholder')}
        disabled={disabled}
        aria-invalid={urlNotAllowed}
        style={{
          marginBottom: urlNotAllowed ? 2 : 6,
          ...(urlNotAllowed ? { borderColor: 'var(--destructive)' } : {}),
        }}
      />
      {urlNotAllowed ? (
        <div
          role="alert"
          style={{ fontSize: 12, color: 'var(--destructive)', marginBottom: 6 }}
        >
          {t('blocks.videoUrlNotAllowed')}
        </div>
      ) : null}

      <label
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          fontSize: 12,
          cursor: 'pointer',
          marginBottom: 4,
        }}
      >
        <input
          type="checkbox"
          checked={block.showText}
          onChange={(e) => onChange({ showText: e.target.checked })}
          disabled={disabled}
          style={{ width: 14, height: 14, accentColor: 'var(--info)' }}
        />
        {t('blocks.showText')}
      </label>
      {block.showText ? (
        <textarea
          className="form-field__textarea"
          style={{ minHeight: 60 }}
          value={block.text}
          onChange={(e) => onChange({ text: e.target.value })}
          placeholder={t('blocks.videoDescPlaceholder')}
          disabled={disabled}
        />
      ) : null}
    </div>
  );
}
